// User Configuration — Numeric Formatting (PURE)
//
// Extracted from user-config.service.ts to keep that file under the 500-line
// budget (LLM-first architecture rule). Every function here is PURE: it takes
// the relevant config fields / asset metadata as parameters and NEVER reads
// global state or `this`. UserConfigService's public `formatNumber` and
// `formatNumberWithFixedDecimals` methods delegate to these functions, passing
// their live `this.config` (and, for formatNumber, the resolved asset metadata).

import type { AssetMetadata } from '@/shared/constants/asset-precision'
import type { NumberFormat, UserConfiguration } from '@/shared/types/services.types'

// Cache Intl.NumberFormat instances keyed by decimal count: constructing a
// formatter is comparatively expensive and this runs once per rendered amount
// cell (e.g. every /balances row).
const fixedDecimalCanonicalFormatters = new Map<number, Intl.NumberFormat>()

/**
 * Canonical, NON-grouped, half-expand-rounded decimal string for `num` at
 * exactly `fixedDecimals` places, always with a '.' decimal point ('en-US').
 * Replaces num.toFixed(), which was insufficient in two money-critical ways:
 *   1. |num| >= 1e21 → toFixed emits exponential notation ('1e+21'), which the
 *      thousands-grouping regex cannot group. Intl always emits full digits.
 *   2. Ties round toward the stored IEEE double, not half-expand — e.g.
 *      (1.005).toFixed(2) === '1.00', whereas the rest of the app (Intl) shows
 *      '1.01'. Intl.NumberFormat rounds the shortest round-trippable decimal
 *      with its default roundingMode 'halfExpand', matching every other money
 *      display in the UI.
 * Grouping/decimal separators are applied by the CALLER afterwards.
 */
export function formatCanonicalFixedDecimalString(num: number, fixedDecimals: number): string {
  let formatter = fixedDecimalCanonicalFormatters.get(fixedDecimals)
  if (formatter === undefined) {
    // roundingMode defaults to 'halfExpand' (verified via resolvedOptions on
    // Node 22) — left implicit to avoid depending on the roundingMode lib type.
    formatter = new Intl.NumberFormat('en-US', {
      useGrouping: false,
      minimumFractionDigits: fixedDecimals,
      maximumFractionDigits: fixedDecimals,
    })
    fixedDecimalCanonicalFormatters.set(fixedDecimals, formatter)
  }
  return formatter.format(num)
}

/**
 * Map the active `numberFormat` preference to its thousands/decimal
 * separators. 'compact' intentionally resolves to the configured DEFAULT
 * separators: callers that need exact digits (formatNumberWithFixedDecimals
 * for crypto amounts) must never abbreviate to K/M/B.
 *
 * `fallbackThousandsSeparator` / `fallbackDecimalSeparator` are the user's
 * configured defaults (config.thousandsSeparator / config.decimalSeparator),
 * used by the european/compact/default branch.
 */
export function resolveSeparatorsForNumberFormat(
  numberFormat: NumberFormat,
  fallbackThousandsSeparator: string,
  fallbackDecimalSeparator: string,
): {
  thousandsSeparator: string
  decimalSeparator: string
} {
  switch (numberFormat) {
    case 'europeo-puntos':
      return { thousandsSeparator: '.', decimalSeparator: ',' }
    case 'europeo-sin-puntos':
      return { thousandsSeparator: '', decimalSeparator: ',' }
    case 'americano-comas':
    case 'american':
      return { thousandsSeparator: ',', decimalSeparator: '.' }
    // 'minimal' and 'european_space' are legacy names defined by the `formatters.ts` map
    // ("1234.56" / "1 234,56"). They used to fall through to the configured defaults and
    // render as '.'/',' instead — separators the SERVER's amount-filter parser does not
    // use for them, so a filter typed off such a cell could not be read back. Each now
    // resolves to its own declared meaning.
    case 'americano-sin-comas':
    case 'minimal':
      return { thousandsSeparator: '', decimalSeparator: '.' }
    case 'international':
      return { thousandsSeparator: ' ', decimalSeparator: '.' }
    case 'european_space':
      return { thousandsSeparator: ' ', decimalSeparator: ',' }
    case 'european':
    case 'compact':
    default:
      return {
        thousandsSeparator: fallbackThousandsSeparator,
        decimalSeparator: fallbackDecimalSeparator,
      }
  }
}

/**
 * Unified formatting helper with asset-aware precision.
 * Handles decimal separator consistency and trailing zero stripping.
 */
export function formatWithConfig(
  num: number,
  decimals: number,
  metadata: AssetMetadata,
  thousandsSeparator: string,
  decimalSeparator: string,
): string {
  const fixed = num.toFixed(decimals)
  const [integer, decimal] = fixed.split('.')

  // Add thousands separators to integer part
  const integerPart = (integer || '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator)

  let decimalPart = decimal || ''

  // Strip trailing zeros if configured for this asset type
  if (metadata.stripTrailingZeros && decimalPart) {
    decimalPart = decimalPart.replace(/0+$/, '')
  }

  // Ensure minimum significant decimals
  if (decimalPart.length < metadata.minSignificantDecimals) {
    decimalPart = decimalPart.padEnd(metadata.minSignificantDecimals, '0')
  }

  // Return formatted number with proper decimal separator
  return decimalPart ? `${integerPart}${decimalSeparator}${decimalPart}` : integerPart
}

/**
 * True when `value` is a plain base-10 decimal literal: an optional sign, digits,
 * and at most one '.' (e.g. "123", "-0.5", "123456789012.12345679"). Excludes
 * scientific notation ("1e5"), NaN/Infinity and empty/whitespace strings — those
 * fall back to the JS-double formatting path in formatNumberWithConfig.
 */
function isPlainDecimalString(value: string): boolean {
  return /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(value.trim())
}

// Increment a fixed-width digit string (e.g. a fraction) by 1, reporting overflow.
// '89' → { digits: '90', carry: false }; '99' → { digits: '00', carry: true }.
function incrementFixedWidthDigits(digits: string): { digits: string; carry: boolean } {
  const chars = digits.split('')
  let carry = 1
  for (let i = chars.length - 1; i >= 0 && carry === 1; i--) {
    const sum = (chars[i]?.charCodeAt(0) ?? 48) - 48 + carry
    chars[i] = String(sum % 10)
    carry = sum >= 10 ? 1 : 0
  }
  return { digits: chars.join(''), carry: carry === 1 }
}

// Increment an integer digit string by 1, growing a new leading '1' on overflow.
// '129' → '130'; '999' → '1000'.
function incrementIntegerDigits(digits: string): string {
  const incremented = incrementFixedWidthDigits(digits)
  return incremented.carry ? `1${incremented.digits}` : incremented.digits
}

// Drop leading zeros but keep a single '0' for a zero integer part.
function stripLeadingZeros(digits: string): string {
  const trimmed = digits.replace(/^0+/, '')
  return trimmed === '' ? '0' : trimmed
}

/**
 * Cómo se resuelven los decimales que NO caben en la escala pedida.
 *
 * `'halfExpand'` es el redondeo canónico de la app (el de Intl). `'truncate'`
 * corta hacia cero y existe para las superficies CONTABLES: redondear hacia
 * arriba un importe enseña más de lo que hay, y quien recorta tiene además que
 * SEÑALARLO (#2211). No es una preferencia estética: son dos contratos
 * distintos y quien llama elige a sabiendas.
 */
export type DecimalStringRoundingMode = 'halfExpand' | 'truncate'

/**
 * Round a plain decimal STRING to EXACTLY `fixedDecimals` fraction digits using
 * half-away-from-zero (matching Intl's 'halfExpand', the app's canonical rounding
 * — see formatCanonicalFixedDecimalString), WITHOUT converting through a JS
 * double. This is what lets amounts with more significant digits than a double
 * can hold (~15) keep their exact low-order digits. Returns the sign, the integer
 * digits (no leading zeros, at least "0") and a fraction of exactly
 * `fixedDecimals` digits ('' when fixedDecimals is 0) — mirroring what
 * `num.toFixed(fixedDecimals)` produces, just string-exact.
 */
function roundPlainDecimalStringToFixed(
  value: string,
  fixedDecimals: number,
  roundingMode: DecimalStringRoundingMode = 'halfExpand',
): { sign: string; integerDigits: string; fractionDigits: string } {
  const trimmed = value.trim()
  const negative = trimmed.startsWith('-')
  const unsigned = negative || trimmed.startsWith('+') ? trimmed.slice(1) : trimmed

  const dotIndex = unsigned.indexOf('.')
  const rawInteger = dotIndex === -1 ? unsigned : unsigned.slice(0, dotIndex)
  const rawFraction = dotIndex === -1 ? '' : unsigned.slice(dotIndex + 1)

  // The kept digits plus the FIRST dropped digit decide the rounding. A first
  // dropped digit of 5-9 rounds the kept fraction up (half-away-from-zero).
  const keptFraction = rawFraction.slice(0, fixedDecimals).padEnd(fixedDecimals, '0')
  const firstDroppedDigit = rawFraction.charAt(fixedDecimals) // '' when nothing is dropped
  // En modo 'truncate' el dígito descartado NUNCA sube al de al lado: los
  // decimales que no caben se pierden hacia cero y quien llama lo señala.
  const roundUp =
    roundingMode === 'halfExpand' && firstDroppedDigit >= '5' && firstDroppedDigit <= '9'

  let integerDigits = rawInteger === '' ? '0' : rawInteger
  let fractionDigits = keptFraction
  if (roundUp) {
    const carried = incrementFixedWidthDigits(keptFraction)
    fractionDigits = carried.digits
    // A fraction of all 9s carries across the decimal point into the integer.
    if (carried.carry) {
      integerDigits = incrementIntegerDigits(integerDigits)
    }
  }

  return {
    sign: negative ? '-' : '',
    integerDigits: stripLeadingZeros(integerDigits),
    fractionDigits: fixedDecimals === 0 ? '' : fractionDigits,
  }
}

/**
 * Decimal-safe sibling of formatWithConfig: formats a plain decimal STRING to the
 * asset's display precision WITHOUT routing through a JS double, so amounts with
 * more significant digits than a double can represent (e.g. crypto-scale
 * 123456789012.12345679) keep their exact digits. Produces byte-identical output
 * to formatWithConfig for every value that DOES fit in a double.
 */
export function formatDecimalStringWithConfig(
  value: string,
  decimals: number,
  metadata: AssetMetadata,
  thousandsSeparator: string,
  decimalSeparator: string,
  roundingMode: DecimalStringRoundingMode = 'halfExpand',
): string {
  const { sign, integerDigits, fractionDigits } = roundPlainDecimalStringToFixed(
    value,
    decimals,
    roundingMode,
  )

  // Grouping runs over sign+digits so the \B lookahead never inserts a separator
  // between the '-' and the first digit — identical to formatWithConfig.
  const integerPart = `${sign}${integerDigits}`.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator)

  let decimalPart = fractionDigits

  // Strip trailing zeros if configured for this asset type
  if (metadata.stripTrailingZeros && decimalPart) {
    decimalPart = decimalPart.replace(/0+$/, '')
  }

  // Ensure minimum significant decimals
  if (decimalPart.length < metadata.minSignificantDecimals) {
    decimalPart = decimalPart.padEnd(metadata.minSignificantDecimals, '0')
  }

  return decimalPart ? `${integerPart}${decimalSeparator}${decimalPart}` : integerPart
}

/**
 * Pure core of UserConfigService.formatNumber. Receives the already-resolved
 * asset metadata and the user config, so it never reads `this` or does I/O.
 * Degenerate input follows the public contract: null/undefined → '',
 * unparseable values are echoed back as-is.
 */
export function formatNumberWithConfig(
  value: number | string | null | undefined,
  assetMetadata: AssetMetadata,
  /** Only the separator fields are read — a report can pass its COUNTRY's separators. */
  config: Pick<
    UserConfiguration,
    'numberFormat' | 'thousandsSeparator' | 'decimalSeparator' | 'minDecimals'
  >,
  options?: {
    allowCompactAbbreviation?: boolean
    /**
     * Decimales a pintar en lugar de los del activo. Existe para las
     * superficies que NO pueden redondear un decimal guardado: la precisión del
     * activo es un suelo de legibilidad, no un techo (#2211). Quien lo pasa lo
     * deriva de `resolveLedgerAmountDisplayPrecision`, nunca de un literal.
     */
    displayDecimalsOverride?: number
    /** Cómo se resuelve lo que no cabe. Ver `DecimalStringRoundingMode`. */
    roundingMode?: DecimalStringRoundingMode
  },
): string {
  if (value === null || value === undefined) return ''

  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return value?.toString() ?? ''

  // Get asset-specific precision
  const decimals = options?.displayDecimalsOverride ?? assetMetadata.displayDecimals

  // 'compact' is the only preference that is not a plain separator mapping:
  // it abbreviates to K/M/B instead of grouping thousands. Callers that must
  // never hide the exact magnitude (e.g. an EDITABLE amount cell) pass
  // allowCompactAbbreviation:false to opt out — they still honor the user's
  // separator preference, they just never get a K/M/B abbreviation.
  const allowCompactAbbreviation = options?.allowCompactAbbreviation ?? true
  if (allowCompactAbbreviation && config.numberFormat === 'compact') {
    // Abbreviate by MAGNITUDE: a negative num (e.g. a -5M loss) never
    // satisfies `num >= 1e3`, so we threshold + divide on Math.abs and
    // re-attach the '-' sign, giving '-5.00M' instead of '-5000000.00'.
    const magnitude = Math.abs(num)
    const sign = num < 0 ? '-' : ''
    if (magnitude >= 1e9) return `${sign}${(magnitude / 1e9).toFixed(2)}B`
    if (magnitude >= 1e6) return `${sign}${(magnitude / 1e6).toFixed(2)}M`
    if (magnitude >= 1e3) return `${sign}${(magnitude / 1e3).toFixed(2)}K`
    // Small-number fall-through: num already carries its sign here.
    return num.toFixed(config.minDecimals)
  }

  const { thousandsSeparator, decimalSeparator } = resolveSeparatorsForNumberFormat(
    config.numberFormat,
    config.thousandsSeparator,
    config.decimalSeparator,
  )

  // DECIMAL-SAFE PATH: a plain decimal STRING is the full-precision source of
  // truth (backend numeric columns serialize to strings). Format it directly from
  // the string so amounts with more significant digits than a JS double can hold
  // (~15) keep their exact low-order digits — parseFloat would silently corrupt
  // them (e.g. 123456789012.12345679 → 123456789012.12345886). Numbers (already
  // doubles) and non-plain strings keep the toFixed path below.
  if (typeof value === 'string' && isPlainDecimalString(value)) {
    return formatDecimalStringWithConfig(
      value,
      decimals,
      assetMetadata,
      thousandsSeparator,
      decimalSeparator,
      options?.roundingMode ?? 'halfExpand',
    )
  }

  // El camino del double (un `number`, o una cadena que no es decimal simple) no
  // sabe truncar: `toFixed` redondea y punto. No se finge que sí — un `number`
  // ya perdió los dígitos bajos antes de llegar aquí, así que no hay un decimal
  // guardado que proteger.

  return formatWithConfig(num, decimals, assetMetadata, thousandsSeparator, decimalSeparator)
}

/**
 * Pure core of UserConfigService.formatNumberWithFixedDecimals. Formats with
 * EXACTLY `fixedDecimals` decimals using the separators of the active
 * numberFormat preference. For amount columns that must always render a fixed
 * precision (e.g. spec 4.2.2: 8 decimals for crypto amounts on /balances)
 * while still honoring the user's separator preference. Degenerate input
 * follows the formatNumber contract: null/undefined → '', unparseable values
 * are echoed back as-is.
 */
export function formatNumberWithFixedDecimalsUsingConfig(
  value: number | string | null | undefined,
  fixedDecimals: number,
  config: UserConfiguration,
): string {
  if (value === null || value === undefined) return ''

  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return value?.toString() ?? ''

  const { thousandsSeparator, decimalSeparator } = resolveSeparatorsForNumberFormat(
    config.numberFormat,
    config.thousandsSeparator,
    config.decimalSeparator,
  )

  // Canonical 'en-US' string: '.' decimal, no grouping, half-expand rounding,
  // and — unlike toFixed — never exponential for |num| >= 1e21. See
  // formatCanonicalFixedDecimalString for the two bugs this replaces.
  // The '-' sign stays attached to the integer segment; the \B lookahead never
  // inserts a separator between the sign and the first digit.
  const [integerSegment, decimalSegment] = formatCanonicalFixedDecimalString(
    num,
    fixedDecimals,
  ).split('.')
  const integerWithThousands = (integerSegment ?? '0').replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousandsSeparator,
  )

  return decimalSegment !== undefined && decimalSegment !== ''
    ? `${integerWithThousands}${decimalSeparator}${decimalSegment}`
    : integerWithThousands
}
